import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FileDown, Loader2, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { PurchaseOrderDetail } from '@/types/purchaseOrders'
import { PoDocument } from './PoDocument'
import { downloadPoPdf } from './downloadPoPdf'
import { usePoDocumentInputs } from './usePoDocumentInputs'

/** The print view of an order, or of one of its revisions. */
export function poPrintHref(id: string, revision?: number | null): string {
  return `/inventory/purchase-orders/${id}/print${revision != null ? `?revision=${revision}` : ''}`
}

/** Resolves once every <img> under `el` has loaded (or failed). */
function imagesLoaded(el: HTMLElement): Promise<void> {
  const pending = [...el.querySelectorAll('img')].filter((img) => !img.complete)
  return Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((done) => {
          img.addEventListener('load', () => done(), { once: true })
          img.addEventListener('error', () => done(), { once: true })
        }),
    ),
  ).then(() => undefined)
}

/**
 * Print and Download PDF for one order (z8nrz7f7dz). Print opens the print
 * view in a new tab; the PDF is drawn from a PoDocument rendered off-screen
 * for the length of the download — the same component, so the same page.
 *
 * `revision` prints that revision's snapshot as it stood ("Rev n").
 */
export function PoPrintActions({ po, revision }: { po: PurchaseOrderDetail; revision?: number | null }) {
  const [rendering, setRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const started = useRef(false)
  const inputs = usePoDocumentInputs(po)

  // The template could not be loaded: say so instead of leaving the button
  // stuck on "rendering" with nothing to draw.
  useEffect(() => {
    if (!rendering || !inputs.error) return
    setError('The PO template could not be loaded, so the PDF cannot be made.')
    setRendering(false)
  }, [rendering, inputs.error])

  useEffect(() => {
    if (!rendering || inputs.isLoading || !inputs.layout) return
    // The logo arrives as an object URL a beat after the layout; give it
    // a frame to mount, then wait for it to decode before rasterising.
    let cancelled = false
    const id = requestAnimationFrame(async () => {
      const el = ref.current
      if (!el || cancelled || started.current) return
      // Once rasterising starts it cannot be stopped; a re-run of this effect
      // (the logo URL arriving late) must not start a second download.
      started.current = true
      try {
        await imagesLoaded(el)
        await downloadPoPdf(el, po.revision > 0 ? `${po.number}-rev${po.revision}` : po.number, inputs.layout!.document.paperSize)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'The PDF could not be made.')
      } finally {
        started.current = false
        setRendering(false)
      }
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [rendering, inputs.isLoading, inputs.layout, inputs.logoUrl, po.number, po.revision])

  return (
    <>
      <div className='flex items-center gap-2'>
        <Button size='sm' variant='outline' className='gap-1' asChild>
          <a href={poPrintHref(po.id, revision)} target='_blank' rel='noreferrer'>
            <Printer className='h-4 w-4' />
            Print
          </a>
        </Button>
        <Button
          size='sm'
          variant='outline'
          className='gap-1'
          disabled={rendering}
          onClick={() => {
            setError(null)
            setRendering(true)
          }}
        >
          {rendering ? <Loader2 className='h-4 w-4 animate-spin' /> : <FileDown className='h-4 w-4' />}
          Download PDF
        </Button>
        {error && <span className='text-xs text-destructive'>{error}</span>}
      </div>

      {rendering &&
        inputs.layout &&
        createPortal(
          // Off-screen, not hidden: html2canvas needs laid-out boxes.
          <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0 }}>
            <PoDocument
              ref={ref}
              layout={inputs.layout}
              po={po}
              logoUrl={inputs.logoUrl}
              attributes={inputs.attributes}
              watermark={po.status === 'DRAFT' ? 'DRAFT' : undefined}
              mode='preview'
            />
          </div>,
          document.body,
        )}
    </>
  )
}
